import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
  type User,
  type Unsubscribe,
} from 'firebase/auth';
import { get, ref, set } from 'firebase/database';
import { getFirebaseAuth, getFirebaseDb, isFirebaseConfigured } from '@/lib/firebase';
import type { SupportAgent } from '@/types/support';

export function subscribeAuth(callback: (user: User | null) => void): Unsubscribe {
  if (!isFirebaseConfigured()) {
    callback(null);
    return () => undefined;
  }
  return onAuthStateChanged(getFirebaseAuth(), callback);
}

export async function ensureAnonymousAuth(): Promise<User> {
  const auth = getFirebaseAuth();
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
}

export async function signInAgent(email: string, password: string): Promise<User> {
  const cred = await signInWithEmailAndPassword(getFirebaseAuth(), email.trim(), password);
  return cred.user;
}

export async function signOutUser(): Promise<void> {
  await signOut(getFirebaseAuth());
}

export function getCurrentUser(): User | null {
  if (!isFirebaseConfigured()) return null;
  return getFirebaseAuth().currentUser;
}

export async function isAdmin(uid: string): Promise<boolean> {
  const snap = await get(ref(getFirebaseDb(), `admins/${uid}`));
  return snap.val() === true;
}

export async function getAgentProfile(uid: string): Promise<SupportAgent | null> {
  const snap = await get(ref(getFirebaseDb(), `agents/${uid}`));
  if (!snap.exists()) return null;
  return { agentId: uid, ...snap.val() } as SupportAgent;
}

export async function requireActiveAgent(uid: string): Promise<SupportAgent> {
  const agent = await getAgentProfile(uid);
  if (!agent || !agent.active) {
    throw new Error('You are not an active support agent.');
  }
  return agent;
}

/** Admin-only: create Auth user + agent profile. Caller must already be signed in as admin. */
export async function createAgentAuthUser(
  email: string,
  password: string,
  displayName: string,
): Promise<string> {
  // Creating a second user signs the admin out of the current session in client SDK.
  // Prefer creating users in Firebase Console, or use a secondary Auth app instance.
  const secondaryAppName = `agent-create-${Date.now()}`;
  const { initializeApp, deleteApp, getApps } = await import('firebase/app');
  const { getAuth: getSecondaryAuth } = await import('firebase/auth');
  const { firebaseConfig } = await import('@/config');

  const existing = getApps().find((a) => a.name === secondaryAppName);
  const secondary = existing ?? initializeApp(firebaseConfig, secondaryAppName);
  const secondaryAuth = getSecondaryAuth(secondary);

  try {
    const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
    await signOut(secondaryAuth);
    return cred.user.uid;
  } finally {
    await deleteApp(secondary);
  }
}

export async function upsertCustomerProfile(
  uid: string,
  data: { displayName?: string | null; lastPageUrl?: string },
): Promise<void> {
  await set(ref(getFirebaseDb(), `customers/${uid}`), {
    uid,
    displayName: data.displayName ?? null,
    lastPageUrl: data.lastPageUrl ?? null,
    updatedAt: Date.now(),
  });
}
